/**
 * API Client
 * Centralized Axios instance with interceptors for auth and workspace headers
 *
 * Authentication Architecture (HTTP-only cookies):
 * - Tokens are stored in HTTP-only cookies (frontend has no access)
 * - Backend/Gateway automatically refreshes access tokens using refresh token cookie
 * - 401 response means user is not authenticated (no token refresh possible from frontend)
 * - 401 handling: error propagates → checkAuth sets isAuthenticated=false → ProtectedRoute redirects
 */
import axios from 'axios';
import type {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { API_CONFIG, WORKSPACE_HEADER } from '@/config/api';
import { useGeoMarkupStore } from '@/store';
import { transformApiError } from './apiErrors';

// Create Axios instance
export const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  withCredentials: true, // HTTP-only cookies for auth
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add X-Workspace-Id header
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const workspaceId = useGeoMarkupStore.getState().auth.currentWorkspaceId;

  if (workspaceId && config.headers) {
    config.headers[WORKSPACE_HEADER] = workspaceId;
  }

  return config;
});

// Response interceptor - Transform errors, detect session expiry, let errors propagate
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Detect session expiry: 401 on any request except the initial auth check
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes('/auth/me')
    ) {
      useGeoMarkupStore.getState().auth.setSessionExpired();
    }
    return Promise.reject(transformApiError(error));
  }
);

export default apiClient;
